import React, { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight, FileWarning } from 'lucide-react';
import { Separator } from '../ui/separator';

const GAPS = [
  { key: 'statement_gap', label: 'Bank balance vs lines we hold', tone: 'amber' },
  { key: 'gl_scope_gap', label: 'Journals outside every bucket', tone: 'rose' },
  { key: 'bank_scope_gap', label: 'Bank lines outside every bucket', tone: 'rose' },
  { key: 'matched_difference', label: 'Matched pairs with amount differences', tone: 'amber' },
  { key: 'legacy_lines_without_native_amount', label: 'Foreign-currency lines missing native amount', tone: 'slate' },
];

const TONE = {
  amber: 'text-amber-700', rose: 'text-rose-700', slate: 'text-slate-600',
};

function GapRow({ gap, def, formatAmount }) {
  const [open, setOpen] = useState(false);
  const rows = gap.rows || [];
  const amount = gap.amount;
  const hasAmount = amount != null && Math.abs(amount) >= 0.01;
  if (!hasAmount && !(gap.count > 0)) return null;
  return (
    <>
      <Separator className="my-1" />
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex justify-between pl-3 items-center text-left" data-testid={`recon-gap-${def.key}`}>
        <span className={`${TONE[def.tone]} flex items-center gap-1`}>
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <FileWarning className="w-3 h-3" />
          {def.label}{gap.count ? ` (${gap.count})` : ''}
        </span>
        {hasAmount && <span className={`${TONE[def.tone]} font-medium`} data-testid={`recon-gap-${def.key}-amount`}>{formatAmount(Math.abs(amount))}</span>}
      </button>
      {gap.guidance && <p className={`text-[10px] pl-3 ${TONE[def.tone]} opacity-90`}>{gap.guidance}</p>}
      {open && rows.length > 0 && (
        <div className="pl-3 mt-1 max-h-48 overflow-auto rounded border border-slate-200 bg-white" data-testid={`recon-gap-${def.key}-rows`}>
          <table className="w-full text-[10px]">
            <tbody>
              {rows.slice(0, 50).map((r, i) => (
                <tr key={r.journal_id || r.bank_tx_id || i} className="border-b last:border-0 border-slate-100">
                  <td className="px-2 py-1 whitespace-nowrap text-slate-500">{r.date}</td>
                  <td className="px-2 py-1 truncate max-w-[200px]" title={r.description}>{r.description || r.source_type || r.reason}</td>
                  <td className="px-2 py-1 text-slate-500 truncate max-w-[160px]" title={r.reason}>{r.reason || r.status || ''}</td>
                  <td className="px-2 py-1 text-right font-mono whitespace-nowrap">{formatAmount(Math.abs(r.amount ?? r.difference ?? r.functional_amount ?? 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Named scope-gap buckets from the reconciliation engine (iter1450) — every unit of variance, row by row. */
export function ScopeGapExplainers({ scopeGaps, formatAmount }) {
  if (!scopeGaps) return null;
  const visible = GAPS.filter(d => scopeGaps[d.key] && ((Math.abs(scopeGaps[d.key].amount || 0) >= 0.01) || scopeGaps[d.key].count > 0));
  if (!visible.length) return null;
  return (
    <div data-testid="recon-scope-gaps">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 mt-2 pl-3">
        <AlertCircle className="w-3 h-3" /> Needs your action
      </div>
      {visible.map(def => <GapRow key={def.key} def={def} gap={scopeGaps[def.key]} formatAmount={formatAmount} />)}
    </div>
  );
}
